import { configureAvatarFetcher, invalidateAvatar } from 'klayowebshared';
import { AxiosError } from 'axios';
import axiosClient, { ExtendedAxiosRequestConfig } from './AxiosClient';
import { EventBus } from './EventBus';

const NO_PHOTO_STATUSES = [400, 403, 404, 410];

/**
 * Routes every profile photo through the authenticated API client (see klayowebshared avatarStore)
 * and refreshes a photo after its owner uploads a new one.
 */
export function setupAvatarFetcher(): void {
  configureAvatarFetcher(async (employeeId: string, version: number) => {
    const config: ExtendedAxiosRequestConfig = {
      // `v` changes after an upload, so the browser cannot serve the replaced photo from its cache.
      params: version ? { employeeId, v: version } : { employeeId },
      responseType: 'blob',
      // All photos share one URL, so the default method+URL key would abort every load but the last.
      requestKey: `avatar:${employeeId}`,
      // A missing or blocked photo falls back to initials; it is not an error worth a snackbar.
      showSnackbar: false,
      // 202 = the malware scan is still pending.
      validateStatus: (s) => s === 200 || s === 202,
    };
    try {
      const res = await axiosClient.get<Blob>('/Profile/Avatar', config);
      return res.status === 202 ? undefined : res.data;
    } catch (error) {
      // No photo, another organization's employee, or a blocked file: settled, show initials.
      // Anything else (429, 5xx, network) rethrows so the store retries it later.
      if (NO_PHOTO_STATUSES.includes((error as AxiosError).response?.status ?? 0)) return null;
      throw error;
    }
  });

  new EventBus().on(EventBus.event.profilePictureChanged, (employeeId?: string) => {
    if (employeeId) invalidateAvatar(employeeId);
  });
}
